import {isValidDate} from 'ApplicationEntry/models/Date';
import {EmptyPhoneNumber, isValidPhoneNumber, PhoneNumber, ValidPhoneNumber} from 'ApplicationEntry/models/PhoneNumber';
import {
  EmptySocialSecurityNumber,
  isValidSocialSecurityNumber,
  SocialSecurityNumber,
  UnvalidSocialSecurityNumber,
  ValidSocialSecurityNumber,
} from 'ApplicationEntry/models/SocialSecurityNumber';
import {EmptyString, NonEmptyString, StringUnion} from 'Core/models/EmptyString';
import {ToPartialMatchOptions} from 'Shared/utilities/MapDiscriminatedUnion';
import {EmptyZipCode, isValidZipCode, UnvalidatedZipCode, ValidZipCode, ZipCode} from './ZipCode';

export const relations = [
  'Son',
  'Daughter',
  'Stepson',
  'Stepdaughter',
  'Spouse',
  'Child',
  'Granddaughter',
  'Grandson',
  'Domestic Partner',
  'Reciprocal Beneficiary',
  'Designated Beneficiary',
  'Other',
];

export enum PrimaryBeneficiaryActionType {
  ADD_PRIMARYBENEFICIARY_REDUCER = 'primaryBeneficiary/ADD_PRIMARYBENEFICIARY_REDUCER',
  REMOVE_PRIMARYBENEFICIARY_REDUCER = 'primaryBeneficiary/REMOVE_PRIMARYBENEFICIARY_REDUCER',
  EMPTY_PRIMARYBENEFICIARY_REDUCER = 'primaryBeneficiary/EMPTY_PRIMARYBENEFICIARY_REDUCER',
  UPDATE_FIRST_NAME_REDUCER = 'primaryBeneficiary/UPDATE_FIRST_NAME_REDUCER',
  UPDATE_MIDDLE_NAME_REDUCER = 'primaryBeneficiary/UPDATE_MIDDLE_NAME_REDUCER',
  UPDATE_LAST_NAME_REDUCER = 'primaryBeneficiary/UPDATE_LAST_NAME_REDUCER',
  UPDATE_RELATIONSHIP_REDUCER = 'primaryBeneficiary/UPDATE_RELATIONSHIP_REDUCER',
  UPDATE_PERCENT_REDUCER = 'primaryBeneficiary/UPDATE_PERCENT_REDUCER',
  UPDATE_SSN_REDUCER = 'primaryBeneficiary/UPDATE_SSN_REDUCER',
  UPDATE_DOB_REDUCER = 'primaryBeneficiary/UPDATE_DOB_REDUCER',
  UPDATE_PHONE_REDUCER = 'primaryBeneficiary/UPDATE_PHONE_REDUCER',
  UPDATE_ADDRESS_REDUCER = 'primaryBeneficiary/UPDATE_ADDRESS_REDUCER',
  UPDATE_ADDRESS_LINE2_REDUCER = 'primaryBeneficiary/UPDATE_ADDRESS_LINE2_REDUCER',
  UPDATE_CITY_REDUCER = 'primaryBeneficiary/UPDATE_CITY_REDUCER',
  UPDATE_STATE_REDUCER = 'primaryBeneficiary/UPDATE_STATE_REDUCER',
  UPDATE_ZIP_REDUCER = 'primaryBeneficiary/UPDATE_ZIP_REDUCER',
  EDIT_PRIMARYBENEFICIARY_REDUCER = 'primaryBeneficiary/EDIT_PRIMARYBENEFICIARY_REDUCER',
  GET_TOTAL_PERCENT_LOCAL = 'primaryBeneficiary/GET_TOTAL_PERCENT_LOCAL',
  UPDATE_SIGNATURE_DATE_REDUCER = 'primaryBeneficiary/UPDATE_SIGNATURE_DATE_REDUCER',
}

export class UnvalidPrimaryBeneficiary {
  public readonly key = 'UnvalidPrimaryBeneficiary';

  constructor(
    public readonly firstName: StringUnion,
    public readonly middleName: StringUnion,
    public readonly lastName: StringUnion,
    public readonly relationship: StringUnion,
    public readonly percent: number,
    public readonly socialSecurityNumber: SocialSecurityNumber,
    public readonly dob: Date | '',
    public readonly phone: PhoneNumber,
    public readonly address: StringUnion,
    public readonly addressLine2: StringUnion,
    public readonly city: StringUnion,
    public readonly state: StringUnion,
    public readonly zip: ZipCode,
    public readonly occSmokerCode: StringUnion,
    public readonly signatureDate: Date | '',
  ) {}
}

export class InvalidPrimaryBeneficiary {
  public readonly key = 'InvalidPrimaryBeneficiary';

  constructor(
    public readonly firstName: StringUnion,
    public readonly middleName: StringUnion,
    public readonly lastName: StringUnion,
    public readonly relationship: StringUnion,
    public readonly percent: number,
    public readonly socialSecurityNumber: SocialSecurityNumber,
    public readonly dob: Date | '',
    public readonly phone: PhoneNumber,
    public readonly address: StringUnion,
    public readonly addressLine2: StringUnion,
    public readonly city: StringUnion,
    public readonly state: StringUnion,
    public readonly zip: ZipCode,
    public readonly occSmokerCode: StringUnion,
    public readonly signatureDate: Date | '',
  ) {}
}

export class ValidPrimaryBeneficiary {
  public static create(
    firstName: StringUnion,
    middleName: StringUnion,
    lastName: StringUnion,
    relationship: StringUnion,
    percent: number,
    socialSecurityNumber: SocialSecurityNumber,
    dob: Date | '',
    phone: PhoneNumber,
    address: StringUnion,
    addressLine2: StringUnion,
    city: StringUnion,
    state: StringUnion,
    zip: ZipCode,
    occSmokerCode: StringUnion,
    signatureDate: Date | '',
  ) {
    return isValidSocialSecurityNumber(socialSecurityNumber) &&
      isValidPhoneNumber(phone) &&
      isValidZipCode(zip) &&
      isValidDate(dob) &&
      !(firstName.value === '' || lastName.value === '' || relationship.value === '')
      ? new ValidPrimaryBeneficiary(
          firstName,
          middleName,
          lastName,
          relationship,
          percent,
          socialSecurityNumber,
          dob,
          phone,
          address,
          addressLine2,
          city,
          state,
          zip,
          occSmokerCode,
          signatureDate,
        )
      : new InvalidPrimaryBeneficiary(
          firstName,
          middleName,
          lastName,
          relationship,
          percent,
          socialSecurityNumber,
          dob,
          phone,
          address,
          addressLine2,
          city,
          state,
          zip,
          occSmokerCode,
          signatureDate
        );
  }

  public readonly key = 'ValidPrimaryBeneficiary';
  private constructor(
    public readonly firstName: StringUnion,
    public readonly middleName: StringUnion,
    public readonly lastName: StringUnion,
    public readonly relationship: StringUnion,
    public readonly percent: number,
    public readonly socialSecurityNumber: ValidSocialSecurityNumber | UnvalidSocialSecurityNumber,
    public readonly dob: Date | '',
    public readonly phone: ValidPhoneNumber,
    public readonly address: StringUnion,
    public readonly addressLine2: StringUnion,
    public readonly city: StringUnion,
    public readonly state: StringUnion,
    public readonly zip: ValidZipCode | UnvalidatedZipCode,
    public readonly occSmokerCode: StringUnion,
    public readonly signatureDate: Date | '',
  ) {}
}

export type PrimaryBeneficiary = InvalidPrimaryBeneficiary | ValidPrimaryBeneficiary | UnvalidPrimaryBeneficiary;

export type PrimaryBeneficiaryOptions<TReturn> = ToPartialMatchOptions<PrimaryBeneficiary, 'key', TReturn>;

export const matchPrimaryBeneficiary = <TReturn>(options: PrimaryBeneficiaryOptions<TReturn>) => (
  app: PrimaryBeneficiary
): TReturn => {
  switch (app.key) {
    case 'InvalidPrimaryBeneficiary':
      return options.InvalidPrimaryBeneficiary ? options.InvalidPrimaryBeneficiary(app) : options.default(app);
    case 'ValidPrimaryBeneficiary':
      return options.ValidPrimaryBeneficiary ? options.ValidPrimaryBeneficiary(app) : options.default(app);
    case 'UnvalidPrimaryBeneficiary':
      return options.UnvalidPrimaryBeneficiary ? options.UnvalidPrimaryBeneficiary(app) : options.default(app);
    default:
      return options.default(app);
  }
};

export const changeFirstName = (firstName: string) => ({
  middleName,
  lastName,
  relationship,
  percent,
  socialSecurityNumber,
  dob,
  phone,
  address,
  addressLine2,
  city,
  state,
  zip,
  occSmokerCode,
  signatureDate,
}: PrimaryBeneficiary) =>
  ValidPrimaryBeneficiary.create(
    NonEmptyString.create(firstName),
    middleName,
    lastName,
    relationship,
    percent,
    socialSecurityNumber,
    dob,
    phone,
    address,
    addressLine2,
    city,
    state,
    zip,
    occSmokerCode,
    signatureDate
  );
export const changeMiddleName = (middleName: string) => ({
  firstName,
  lastName,
  relationship,
  percent,
  socialSecurityNumber,
  dob,
  phone,
  address,
  addressLine2,
  city,
  state,
  zip,
  occSmokerCode,
  signatureDate,
}: PrimaryBeneficiary) =>
  ValidPrimaryBeneficiary.create(
    firstName,
    NonEmptyString.create(middleName),
    lastName,
    relationship,
    percent,
    socialSecurityNumber,
    dob,
    phone,
    address,
    addressLine2,
    city,
    state,
    zip,
    occSmokerCode,
    signatureDate
  );
export const changeLastName = (lastName: string) => ({
  firstName,
  middleName,
  relationship,
  percent,
  socialSecurityNumber,
  dob,
  phone,
  address,
  addressLine2,
  city,
  state,
  zip,
  occSmokerCode,
  signatureDate,
}: PrimaryBeneficiary) =>
  ValidPrimaryBeneficiary.create(
    firstName,
    middleName,
    NonEmptyString.create(lastName),
    relationship,
    percent,
    socialSecurityNumber,
    dob,
    phone,
    address,
    addressLine2,
    city,
    state,
    zip,
    occSmokerCode,
    signatureDate
  );
export const changeRelationship = (relationship: string) => ({
  firstName,
  middleName,
  lastName,
  percent,
  socialSecurityNumber,
  dob,
  phone,
  address,
  addressLine2,
  city,
  state,
  zip,
  occSmokerCode,
  signatureDate,
}: PrimaryBeneficiary) =>
  ValidPrimaryBeneficiary.create(
    firstName,
    middleName,
    lastName,
    NonEmptyString.create(relationship),
    percent,
    socialSecurityNumber,
    dob,
    phone,
    address,
    addressLine2,
    city,
    state,
    zip,
    occSmokerCode,
    signatureDate
  );

export const changePercent = (percent: number) => ({
  firstName,
  middleName,
  lastName,
  relationship,
  socialSecurityNumber,
  dob,
  phone,
  address,
  addressLine2,
  city,
  state,
  zip,
  occSmokerCode,
  signatureDate,
}: PrimaryBeneficiary) =>
  ValidPrimaryBeneficiary.create(
    firstName,
    middleName,
    lastName,
    relationship,
    percent,
    socialSecurityNumber,
    dob,
    phone,
    address,
    addressLine2,
    city,
    state,
    zip,
    occSmokerCode,
    signatureDate
  );

export const changeSSN = (socialSecurityNumber: string) => ({
  firstName,
  middleName,
  lastName,
  relationship,
  percent,
  dob,
  phone,
  address,
  addressLine2,
  city,
  state,
  zip,
  occSmokerCode,
  signatureDate,
}: PrimaryBeneficiary) =>
  ValidPrimaryBeneficiary.create(
    firstName,
    middleName,
    lastName,
    relationship,
    percent,
    ValidSocialSecurityNumber.create(socialSecurityNumber),
    dob,
    phone,
    address,
    addressLine2,
    city,
    state,
    zip,
    occSmokerCode,
    signatureDate
  );

export const changeDOB = (dob: Date | '') => ({
  firstName,
  middleName,
  lastName,
  relationship,
  percent,
  socialSecurityNumber,
  phone,
  address,
  addressLine2,
  city,
  state,
  zip,
  occSmokerCode,
  signatureDate,
}: PrimaryBeneficiary) =>
  ValidPrimaryBeneficiary.create(
    firstName,
    middleName,
    lastName,
    relationship,
    percent,
    socialSecurityNumber,
    dob,
    phone,
    address,
    addressLine2,
    city,
    state,
    zip,
    occSmokerCode,
    signatureDate
  );

export const changePhone = (phone: PhoneNumber) => ({
  firstName,
  middleName,
  lastName,
  relationship,
  percent,
  socialSecurityNumber,
  dob,
  address,
  addressLine2,
  city,
  state,
  zip,
  occSmokerCode,
  signatureDate,
}: PrimaryBeneficiary) =>
  ValidPrimaryBeneficiary.create(
    firstName,
    middleName,
    lastName,
    relationship,
    percent,
    socialSecurityNumber,
    dob,
    phone,
    address,
    addressLine2,
    city,
    state,
    zip,
    occSmokerCode,
    signatureDate
  );

export const changeAddress = (address: string) => ({
  firstName,
  middleName,
  lastName,
  relationship,
  percent,
  socialSecurityNumber,
  dob,
  phone,
  addressLine2,
  city,
  state,
  zip,
  occSmokerCode,
  signatureDate,
}: PrimaryBeneficiary) =>
  ValidPrimaryBeneficiary.create(
    firstName,
    middleName,
    lastName,
    relationship,
    percent,
    socialSecurityNumber,
    dob,
    phone,
    NonEmptyString.create(address),
    addressLine2,
    city,
    state,
    zip,
    occSmokerCode,
    signatureDate
  );

export const changeAddressLine2 = (addressLine2: string) => ({
  firstName,
  middleName,
  lastName,
  relationship,
  percent,
  socialSecurityNumber,
  dob,
  phone,
  address,
  city,
  state,
  zip,
  occSmokerCode,
  signatureDate,
}: PrimaryBeneficiary) =>
  ValidPrimaryBeneficiary.create(
    firstName,
    middleName,
    lastName,
    relationship,
    percent,
    socialSecurityNumber,
    dob,
    phone,
    address,
    NonEmptyString.create(addressLine2),
    city,
    state,
    zip,
    occSmokerCode,
    signatureDate
  );

export const changeCity = (city: string) => ({
  firstName,
  middleName,
  lastName,
  relationship,
  percent,
  socialSecurityNumber,
  dob,
  phone,
  address,
  addressLine2,
  state,
  zip,
  occSmokerCode,
  signatureDate,
}: PrimaryBeneficiary) =>
  ValidPrimaryBeneficiary.create(
    firstName,
    middleName,
    lastName,
    relationship,
    percent,
    socialSecurityNumber,
    dob,
    phone,
    address,
    addressLine2,
    NonEmptyString.create(city),
    state,
    zip,
    occSmokerCode,
    signatureDate
  );

export const changeState = (state: string) => ({
  firstName,
  middleName,
  lastName,
  relationship,
  percent,
  socialSecurityNumber,
  dob,
  phone,
  address,
  addressLine2,
  city,
  zip,
  occSmokerCode,
  signatureDate,
}: PrimaryBeneficiary) =>
  ValidPrimaryBeneficiary.create(
    firstName,
    middleName,
    lastName,
    relationship,
    percent,
    socialSecurityNumber,
    dob,
    phone,
    address,
    addressLine2,
    city,
    NonEmptyString.create(state),
    zip,
    occSmokerCode,
    signatureDate
  );

export const changeZip = (zip: ZipCode) => ({
  firstName,
  middleName,
  lastName,
  relationship,
  percent,
  socialSecurityNumber,
  dob,
  phone,
  address,
  addressLine2,
  city,
  state,
  occSmokerCode,
  signatureDate,
}: PrimaryBeneficiary) =>
  ValidPrimaryBeneficiary.create(
    firstName,
    middleName,
    lastName,
    relationship,
    percent,
    socialSecurityNumber,
    dob,
    phone,
    address,
    addressLine2,
    city,
    state,
    zip,
    occSmokerCode,
    signatureDate
  );

export const changeOccSmokerCode = (occSmokerCode: string) => ({
  firstName,
  middleName,
  lastName,
  relationship,
  percent,
  socialSecurityNumber,
  dob,
  phone,
  address,
  addressLine2,
  city,
  state,
  zip,
  signatureDate,
}: PrimaryBeneficiary) =>
  ValidPrimaryBeneficiary.create(
    firstName,
    middleName,
    lastName,
    relationship,
    percent,
    socialSecurityNumber,
    dob,
    phone,
    address,
    addressLine2,
    city,
    state,
    zip,
    NonEmptyString.create(occSmokerCode),
    signatureDate
  );

export const changeSignatureDate = (signatureDate: Date | '') => ({
  firstName,
  middleName,
  lastName,
  relationship,
  percent,
  socialSecurityNumber,
  dob,
  phone,
  address,
  addressLine2,
  city,
  state,
  zip,
  occSmokerCode,
}: PrimaryBeneficiary) =>
  ValidPrimaryBeneficiary.create(
    firstName,
    middleName,
    lastName,
    relationship,
    percent,
    socialSecurityNumber,
    dob,
    phone,
    address,
    addressLine2,
    city,
    state,
    zip,
    occSmokerCode,
    signatureDate
  );

export const updatePrimaryBeneficiaryValues = (
  firstName: StringUnion,
  middleName: StringUnion,
  lastName: StringUnion,
  relationship: StringUnion,
  percent: number,
  socialSecurityNumber: string,
  dob: Date | '',
  phone: PhoneNumber,
  address: StringUnion,
  addressLine2: StringUnion,
  city: StringUnion,
  state: StringUnion,
  zip: ZipCode,
  occSmokerCode: StringUnion,
  signatureDate: Date | '',
) => () =>
  ValidPrimaryBeneficiary.create(
    firstName,
    middleName,
    lastName,
    relationship,
    percent,
    ValidSocialSecurityNumber.create(socialSecurityNumber),
    dob,
    phone,
    address,
    addressLine2,
    city,
    state,
    zip,
    occSmokerCode,
    signatureDate
  );

export const EmptyPrimaryBeneficiary = new UnvalidPrimaryBeneficiary(
  new EmptyString(),
  new EmptyString(),
  new EmptyString(),
  new EmptyString(),
  0,
  EmptySocialSecurityNumber,
  '',
  EmptyPhoneNumber,
  new EmptyString(),
  new EmptyString(),
  new EmptyString(),
  new EmptyString(),
  EmptyZipCode,
  new EmptyString(),
  ''
);

export const isValidPrimaryBeneficiary = (ele: PrimaryBeneficiary): ele is ValidPrimaryBeneficiary => {
  return ele.key === 'ValidPrimaryBeneficiary' || ele.key === 'UnvalidPrimaryBeneficiary';
};
